import { Stack, Chip, Typography } from '@mui/material';

import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useParams } from 'react-router-dom';

import { ReactComponent as DrawerClose } from 'common/assets/images/drawer-close.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import TextFieldComponent from 'common/components/TextField/TextField';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	APP_GENERAL_ERROR_MSG,
	PERMISSION_DENIED_ERROR_MSG,
} from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';

import { updateEntityTag } from 'features/RiskProfile/Taxonomy/api';

import styles from './Overview.module.scss';

interface ITagProps {
	entityTags: any[] | undefined;
	setEntityTags: any;
	setIsTagUpdated: any;
	isTagUpdated: boolean;
	refreshTagHandler: () => void;
	resetOnError: () => void;
}
const Tags = (props: ITagProps) => {
	const {
		entityTags,
		setEntityTags,
		setIsTagUpdated,
		isTagUpdated,
		refreshTagHandler,
		resetOnError,
	} = props;
	const dispatch = useDispatch();
	const [tag, setTag] = useState('');
	const [error, setError] = useState('');

	const selectedEntity = useSelector(
		(state: RootState) => state.common.selectedEntityTypeData,
	);
	const { id, type } = selectedEntity;

	/// If group page, create options will not be displayed
	const { groupId } = useParams();
	const { drawerCloseIconColor } = styles;
	const { OK, FORBIDDEN } = HTTP_STATUS_CODES;

	const onChangeValue = (event: any) => {
		setTag(event.target.value.trim());
		setError('');
	};

	const addClickHandler = async () => {
		setEntityTags((prevState: any) => {
			const prevData = [...prevState];
			prevData.push({
				id: prevData.length,
				tagText: tag,
			});
			return prevData;
		});
		setIsTagUpdated(true);
		setTag('');
	};

	const deleteTagHandler = async (el: any) => {
		setEntityTags((prevState: any) => {
			let prevData = [...prevState];
			prevData = prevData.filter((tag: any) => tag.tagText !== el.tagText);
			return prevData;
		});
		setIsTagUpdated(true);
	};

	const updateTags = async (updatedTags: string[]) => {
		const response = await updateEntityTag(id, type, updatedTags);
		if (response?.statusCode === OK) {
			setIsTagUpdated(false);
			refreshTagHandler();
		} else if (response?.statusCode === FORBIDDEN) {
			resetOnError();
			dispatch(
				setToastMessage({
					message: PERMISSION_DENIED_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		} else {
			resetOnError();
			dispatch(
				setToastMessage({
					message: APP_GENERAL_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};

	useEffect(() => {
		if (isTagUpdated && entityTags) {
			const tagsArray: string[] = entityTags?.map((element) => element.tagText);
			updateTags(tagsArray);
		}
	}, [isTagUpdated, entityTags]);

	return (
		<div data-testid="tag-container">
			{!groupId && (
				<Stack direction="row">
					<TextFieldComponent
						dataTestId="add-input"
						fullWidth={false}
						value={tag}
						inputProps={{ tabIndex: 1 }}
						type="text"
						onChange={(e) => onChangeValue(e)}
						name="Add"
						helperText={error}
					/>
					<ButtonComp
						clickHandler={addClickHandler}
						btnType="primary"
						disabled={!tag}
						data-testid="addBtn"
						compClass={styles['add-btn']}
					>
						Add
					</ButtonComp>
				</Stack>
			)}

			{!entityTags ||
			entityTags.length === 0 ||
			entityTags.every((tag) => tag.tagText === 'NA') ? (
				<Typography className={styles['no-items-found']}>
					No tags added yet.
				</Typography>
			) : (
				<Stack
					direction="row"
					spacing={1}
					className={styles['tag-list-container']}
				>
					{entityTags?.map((el: any) => {
						if (el.tagText !== 'NA') {
							return (
								<Chip
									key={el.id}
									label={el.tagText}
									className={styles['tag-chip']}
									variant="outlined"
									onDelete={groupId ? undefined : () => deleteTagHandler(el)}
									deleteIcon={
										<DrawerClose
											fill={drawerCloseIconColor}
											data-testid={`close-icon-${el.id}`}
										/>
									}
								/>
							);
						}
						return null;
					})}
				</Stack>
			)}
		</div>
	);
};

export default Tags;
